import { format } from 'date-fns'
import { useActAdminApi } from '@/use/actAdminApi'
import { useErrorHandler } from '@/use/errorHandler'
import { useHelper } from '@/use/helper'
import { useStore } from 'vuex'
import { reportStatuses } from '@/constants/report'

export function useReport() {
  const store = useStore()
  const { initApiInst, actReportsApi } = useActAdminApi()
  const { handleCommonErrors } = useErrorHandler()
  const { escapeString } = useHelper()

  const getReports = async (params) => {
    try {
      await initApiInst()
      return await actReportsApi.value.getReports(params)
    } catch (e) {
      await handleCommonErrors(e)
      return { reports: [], page: 1, total: 0, limit: 10, pages: 0 }
    }
  }

  const getReport = async (reportId) => {
    try {
      await initApiInst()
      return await actReportsApi.value.getReport(reportId)
    } catch (e) {
      await handleCommonErrors(e)
      return null
    }
  }

  const updateReportStatus = async (status, reportsIds, statusType) => {
    const requestData = { reports_ids: reportsIds }
    if (statusType === 'report') requestData.status = status
    if (statusType === 'provider') requestData.provider_status = status
    try {
      await initApiInst()
      const resp = await actReportsApi.value.changeReportsReviewStatus({
        changeReportsReviewStatusRequest: requestData
      })
      if (resp && resp.result && resp.result === 'success') {
        store.commit('alert/add', {
          id: Date.now(),
          text: `Reports ${statusType === 'provider' ? 'provider' : ''} statuses updated successfully`,
          timeout: 3000,
          type: 'success',
          name: 'success_update_report_status'
        })
        return resp
      }
    } catch (e) {
      await handleCommonErrors(e)
      return null
    }
  }

  const getStatusColorClass = (status) => {
    switch (status) {
      case 'inReview':
        return 'text-primary'
      case 'declined':
        return 'text-warning'
      case 'approved':
        return 'text-success'
      default:
        return ''
    }
  }

  const getProcessingStatusColorClass = (status) => {
    switch (status) {
      case 'reassigned':
        return 'text-primary'
      case 'skipped':
        return 'text-gray'
      case 'declined':
        return 'text-warning'
      case 'approved':
      case 'closed':
        return 'text-success'
      case 'failed':
        return 'text-danger'
      default:
        return ''
    }
  }

  const getReportProviderStatusColorClass = (status) => {
    switch (status) {
      case 'requested':
        return 'text-primary'
      case 'declined':
        return 'text-warning'
      case 'approved':
        return 'text-success'
      default:
        return ''
    }
  }

  const getReportReasonNameById = (id) => {
    switch (id) {
      case 1:
        return 'SMS is not coming'
      case 2:
        return 'Wrong SMS received'
      case 3:
        return 'Service not accepting the number'
      case 4:
        return 'Number has been used before'
      case 5:
        return 'Blocked after activation'
      case 6:
        return 'SMS received too late'
      case 7:
        return 'Other reason'
      default:
        return ''
    }
  }

  const getCsvData = (reports) => {
    const reportsArray = Array.isArray(reports) ? reports : [reports]
    const headers = [
      'Date',
      'Number',
      'Activation ID',
      'Service',
      'Country',
      'Reason',
      'Explanation',
      'Evidence Screenshots'
    ]
    const reportsData = reportsArray.map((report) => [
      format(new Date(report.created_at * 1000), 'yyyy-MM-dd HH:mm:ss') || '',
      report.activation.phone || '',
      report.activation.provider_activation_id || '',
      report.activation.provider_service_id || '',
      report.activation.provider_country_id || '',
      getReportReasonNameById(report.reason_id) || '',
      escapeString(report.message || ''),
      report.evidence_url || ''
    ])

    return `${headers.join(',')}\n${reportsData.map((row) => row.join(',')).join('\n')}`
  }

  const getJsonData = (reports) => {
    const formatJsonReport = (report) => ({
      'Date': format(new Date(report.created_at * 1000), 'yyyy-MM-dd HH:mm:ss') || '',
      'Number': report.activation.phone || '',
      'Activation ID': report.activation.provider_activation_id || '',
      'Service': report.activation.provider_service_id || '',
      'Country': report.activation.provider_country_id || '',
      'Reason': getReportReasonNameById(report.reason_id) || '',
      'Explanation': report.message || '',
      'Evidence Screenshots': report.evidence_url || ''
    })

    const reportsData = Array.isArray(reports) ? reports.map(formatJsonReport) : formatJsonReport(reports)

    return JSON.stringify(reportsData, null, 2)
  }

  const getReportUpdateAvailableStatuses = () => {
    return reportStatuses.slice(1).map((status) => {
      if (status.value === 'inReview') {
        return { ...status, disabled: true }
      }
      return status
    })
  }

  return {
    getReports,
    getReport,
    updateReportStatus,
    getStatusColorClass,
    getProcessingStatusColorClass,
    getReportProviderStatusColorClass,
    getReportReasonNameById,
    getCsvData,
    getJsonData,
    getReportUpdateAvailableStatuses
  }
}
